import { useEffect, useRef } from 'react';
import { useSpacetimeDB, useTable } from 'spacetimedb/react';
import { tables as launcherTables, type DbConnection as LauncherConn } from '@sdk/launcher';
import { isAdmin, useRole } from '@/hooks/useRole';

/**
 * Auto-allowlist the current portal origin on the launcher's
 * `my_org_cors_origin` table the first time an admin loads the portal
 * from a hostname the launcher EXE hasn't seen before.
 *
 * The launcher EXE serves a tiny HTTP API at 127.0.0.1:19871 that the
 * portal hits for fast Launch. Browsers do a CORS preflight on that
 * request; the launcher answers `Access-Control-Allow-Origin: <origin>`
 * only when `<origin>` is in `my_org_cors_origin`. New deployed
 * hostnames (e.g. `https://portal.minetwin.ai` on 2026-06-28) aren't
 * automatically in there, so Launch fails with `Failed to fetch` until
 * someone manually allowlists them.
 *
 * This hook closes that gap: when the launcher subscription has
 * applied and the current `window.location.origin` isn't in
 * `my_org_cors_origin` yet, it fires `add_cors_origin` once. The
 * reducer is gated server-side on launcher-admin role, so the call
 * silently fails for non-admins — which is exactly what we want
 * (they aren't authorized to expand the allowlist anyway).
 *
 * Idempotency is handled two ways:
 *   - client-side gate on subscription-ready: `launcherState.isActive`
 *     flips true the moment the WS opens, BUT the initial row payload
 *     for `my_org_cors_origin` hasn't necessarily arrived yet. Without
 *     the `originsReady` gate the effect would fire on every hard
 *     refresh against an empty `origins` array, dispatch a duplicate
 *     `add_cors_origin`, and surface the server's `Origin already in
 *     allowlist` SenderError as a toast (Cloud Lead 2026-06-28).
 *   - client-side `firedRef`: prevents re-firing on every re-render
 *     while the optimistic add propagates and React rebuilds the rows
 *     array reference.
 *
 * Server-side de-dup was NOT a backstop — the `my_org_cors_origin`
 * unique constraint rejects the duplicate with a SenderError rather
 * than silently no-op'ing, so we MUST not dispatch on a duplicate.
 */
// Session-scoped (module-level, survives remounts): origins we already
// dispatched `add_cors_origin` for in this tab. `firedRef` alone resets on
// every remount — the hook's host component remounts on each site switch,
// so a rejected call (e.g. the launcher module doesn't grant this account
// launcher-admin) was re-fired and re-logged on every navigation
// (Cloud Lead 2026-07-27). One attempt per origin per session is enough:
// the server-side allowlist state can't change from our side without a
// role change, which requires a fresh sign-in anyway.
const attemptedOrigins = new Set<string>();

export function useLauncherCorsSelfAllowlist(): void {
  const launcherState = useSpacetimeDB('launcher');
  const [origins, originsReady] = useTable(
    'launcher',
    launcherTables.my_org_cors_origin,
  );
  const role = useRole();
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    if (!launcherState.isActive) return;
    // CRITICAL: wait for the subscription to apply. `isActive` only
    // means the WS is open; the initial table snapshot may still be
    // in flight. Firing now risks a false-negative "origin not in
    // allowlist" → duplicate-insert → toast.
    if (!originsReady) return;
    if (typeof window === 'undefined') return;

    const me = window.location.origin;
    if (origins.some((row) => row.origin === me)) {
      // Already allowlisted — nothing to do. Mark fired so we don't
      // keep re-checking on every re-render.
      firedRef.current = true;
      return;
    }

    // Non-admins can't expand the allowlist — `add_cors_origin` is
    // gated on launcher-admin role server-side. Skip the call so we
    // don't spam the console with denial warnings on every member
    // session. Members of the same org rely on an admin having
    // visited the portal at least once to seed the entry; until
    // then, Launch will fail for them with the underlying CORS error.
    if (!isAdmin(role)) {
      firedRef.current = true;
      return;
    }

    if (attemptedOrigins.has(me)) {
      firedRef.current = true;
      return;
    }

    const conn = launcherState.getConnection() as LauncherConn | null;
    if (!conn) return;

    firedRef.current = true;
    attemptedOrigins.add(me);
    try {
      const result = conn.reducers.addCorsOrigin({ origin: me });
      console.info(`[launcher-cors] requested allowlist for ${me}`);
      // The reducer call returns a Promise that rejects on server-side
      // SenderError. Without a `.catch` the rejection bubbles to the
      // global unhandledrejection handler in App.tsx and becomes a
      // user-visible toast. The two cases we want to swallow silently:
      //   - "Origin already in allowlist" — race we couldn't avoid.
      //   - Auth/role rejection — non-admin case (server-gated).
      // Anything else gets a `console.warn` so it's not invisible.
      const maybePromise = result as unknown as Promise<unknown> | undefined;
      if (maybePromise && typeof maybePromise.catch === 'function') {
        maybePromise.catch((err: unknown) => {
          const msg = err instanceof Error ? err.message : String(err);
          if (/already in allowlist/i.test(msg)) {
            console.info(`[launcher-cors] origin already allowlisted server-side — no-op`);
            return;
          }
          if (/admin role required/i.test(msg)) {
            // The launcher module tracks its own admin roster (separate from
            // srv-console app_role) and doesn't grant this account. Expected
            // for most sessions — say it once, quietly.
            console.info(
              `[launcher-cors] not a launcher admin — skipping self-allowlist (desktop Launch may need an admin to visit once)`,
            );
            return;
          }
          console.warn(`[launcher-cors] add_cors_origin rejected`, err);
        });
      }
    } catch (err) {
      // Reducer call failed at dispatch time — most likely the caller
      // lacks launcher-admin role. Reset firedRef so a re-render with
      // updated state can retry, but log so we don't fail silently.
      firedRef.current = false;
      attemptedOrigins.delete(me);
      console.warn(`[launcher-cors] add_cors_origin dispatch failed`, err);
    }
  }, [launcherState, origins, originsReady, role]);
}
